import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { X, Shield, Clock, AlertCircle } from 'lucide-react';

interface TemporaryAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TemporaryAdminModal: React.FC<TemporaryAdminModalProps> = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { state, enableTemporaryAdmin, disableTemporaryAdmin } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await enableTemporaryAdmin(password);
      if (success) {
        setPassword('');
        onClose();
      } else {
        setError('パスワードが正しくありません');
      }
    } catch (err) {
      setError('エラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = () => {
    disableTemporaryAdmin();
    onClose();
  };

  const getRemainingTime = () => {
    if (!state.temporaryAdminExpiry) return null;
    const now = new Date();
    const expiry = new Date(state.temporaryAdminExpiry);
    const remaining = Math.max(0, Math.floor((expiry.getTime() - now.getTime()) / 1000 / 60));
    return remaining;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <Shield className="mr-2" />
            一時管理者権限
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {state.temporaryAdminMode ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center text-green-800">
                <Shield className="mr-2" size={20} />
                <span className="font-semibold">管理者権限が有効です</span>
              </div>
              <div className="mt-2 flex items-center text-green-600">
                <Clock className="mr-2" size={16} />
                <span>残り時間: {getRemainingTime()}分</span>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="mr-2 text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold">注意事項:</p>
                  <ul className="mt-1 space-y-1 list-disc list-inside">
                    <li>30分後に自動的に無効になります</li>
                    <li>管理者メニューへのアクセスが可能です</li>
                    <li>重要な変更を行う際は注意してください</li>
                  </ul>
                </div>
              </div>
            </div>

            <button
              onClick={handleDisable}
              className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
            >
              管理者権限を無効にする
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                管理者パスワード
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="カツ氏の管理者パスワードを入力"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="mr-2 text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold">説明:</p>
                  <p className="mt-1">
                    スタッフログイン中に一時的に管理者メニューにアクセスできます。
                    30分間有効で、その後自動的に無効になります。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={isLoading || !password}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {isLoading ? '確認中...' : '有効にする'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TemporaryAdminModal;